import { LucideIcon, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface Props {
  label: string;
  value: string | number;
  icon: LucideIcon;
  color: string;
  change?: number | null;
  tooltip?: string;
  semaphore?: 'green' | 'yellow' | 'red' | null;
  badge?: number;
  badgeColor?: string;
  onClick?: () => void;
}

export const HeroMetricCard = ({ label, value, icon: Icon, color, change, tooltip, semaphore, badge, badgeColor, onClick }: Props) => {
  return (
    <Card className={`border-border/50 ${onClick ? 'cursor-pointer hover:bg-muted/30 transition-colors' : ''}`} onClick={onClick}>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center justify-between mb-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-sm text-muted-foreground cursor-default">{label}</p>
              </TooltipTrigger>
              {tooltip && (
                <TooltipContent className="max-w-[220px]">
                  <p className="text-xs">{tooltip}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <div className="flex items-center gap-1.5">
            {badge !== undefined && badge > 0 && (
              <span className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${badgeColor || 'bg-warning/10 text-warning'}`}>
                {badge}
              </span>
            )}
            <Icon className={`h-4 w-4 ${color}`} />
          </div>
        </div>
        <div className="flex items-end gap-2">
          <p className="text-xl font-semibold tracking-tight">{value}</p>
          {semaphore && (
            <span className="mb-0.5">
              {semaphore === 'green' && <CheckCircle2 className="h-4 w-4 text-success" />}
              {semaphore === 'yellow' && <AlertTriangle className="h-4 w-4 text-warning" />}
              {semaphore === 'red' && <AlertTriangle className="h-4 w-4 text-destructive" />}
            </span>
          )}
        </div>
        {change !== undefined && change !== null && (
          <div className={`flex items-center gap-1 mt-1 text-xs ${change >= 0 ? 'text-success' : 'text-destructive'}`}>
            {change >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            <span>{change >= 0 ? '+' : ''}{change.toFixed(0)}% vs prev period</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
